import createReducer from 'arts/config/create_reducer';
import Constants from '../config/const';
import { screenToDefaultDpi } from '../config/utils';

const DEFAULT_ZOOM_POS = 2;

const recalculateDpis = (wall, zoomPosition) => {
  const { width: wWidth, height: wHeight } = wall;
  const { width: sWidth, height: sHeight, whitePos } = Constants.silhouette;
  const width = zoomPosition === DEFAULT_ZOOM_POS ? Math.max(sWidth, wWidth) : wWidth;
  const height = zoomPosition === DEFAULT_ZOOM_POS ? wHeight + (sHeight - whitePos) : wHeight;
  const dpi = screenToDefaultDpi({ width, height });
  const dpis = [dpi * 4, dpi * 2, dpi, dpi / 2];

  return {
    dpis,
    dpi: dpis[zoomPosition]
  };
};

const centerCanvasPosition = (
  { canvasWidth, canvasHeight },
  { width, height }
) => {
  const dx = (canvasWidth - width) / 2;
  const dy = (canvasHeight - height) / 2;
  return {
    posX: -dx,
    posY: -dy,
    centerX: width / 2,
    centerY: height / 2
  };
};

const getDefaultState = () => {
  const {
    wallId,
    currency,
    wallPictures,
    width,
    height,
    currentColor,
    name,
    description,
    sharingUrl,
    keywordList,
    colorList,
    associatedRoomIds,
    associatedHomeDecorStyleIds
  } = window.wall || {};

  return ({
    wallId,
    currency,
    works: wallPictures || [],
    centerX: 0,
    centerY: 0,
    posX: 0,
    posY: 0,
    showSilhouette: true,
    silhouette: Constants.silhouette.default,
    silhouetteX: (width - Constants.silhouette.width) / 2,
    silhouetteY: height - Constants.silhouette.zero,
    currentColor,
    rulerVisible: true,
    guidesVisible: false,
    widthVisible: false,
    heightVisible: false,
    width,
    height,
    name,
    description,
    sharingUrl,
    zoomPosition: DEFAULT_ZOOM_POS,
    ...recalculateDpis(window.wall, DEFAULT_ZOOM_POS),
    keywordList,
    colorList,
    associatedRoomIds,
    associatedHomeDecorStyleIds
  });
};

const updateWork = (works, index, newProps) => {
  const before = works.slice(0, index);
  const after = works.slice(index + 1);
  const updatedWork = { ...works[index], ...newProps };
  const { frameId: newFrameId } = newProps;
  if (newFrameId && newFrameId !== works[index].frameId || updatedWork.frameId === null) {
    updatedWork.nonGlare = false;
  }
  return [...before, updatedWork, ...after];
};

const removeWork = (works, index) => {
  const before = works.slice(0, index);
  const after = works.slice(index + 1);
  return [...before, ...after];
};

const calculateNewPosNoWorks = (state, newDpi) => {
  const {
    centerX, posX, centerY, posY, dpi
  } = state;
  const wpx = (centerX - posX) * 2 * dpi;
  const wInNew = wpx / newDpi;
  const hpx = (centerY - posY) * 2 * dpi;
  const hInNew = hpx / newDpi;
  return {
    ...state,
    posX: 0,
    posY: 0,
    centerX: wInNew / 2,
    centerY: hInNew / 2
  };
};

const calculateNewPos = (state, newDpi) => {
  const {
    dpi, works, posX: posXWas, posY: posYWas, centerX: centerXWas, centerY: centerYWas
  } = state;

  if (!works.length) {
    return calculateNewPosNoWorks(state, newDpi);
  }

  let centerX = 0;
  let centerY = 0;
  works.forEach(({ x, y }) => {
    centerX += parseFloat(x) + 5;
    centerY += parseFloat(y) + 5;
  });
  centerX /= state.works.length;
  centerY /= state.works.length;

  const wpx = (centerXWas - posXWas) * 2 * dpi;
  const wInNew = wpx / newDpi;
  const posX = centerX - (wInNew / 2);
  const hpx = (centerYWas - posYWas) * 2 * dpi;
  const hInNew = hpx / newDpi;
  const posY = centerY - (hInNew / 2);

  return {
    ...state,
    posX,
    posY,
    centerX,
    centerY,
    dpi: newDpi
  };
};

const setZoomPosition = (state, zoomPosition) => {
  const { dpis } = state;
  const dpi = dpis[zoomPosition];
  return ({
    ...calculateNewPos(state, dpi),
    zoomPosition
  });
};

const resetCenter = (state, { canvasWidth, canvasHeight }) => {
  const newState = setZoomPosition(state, DEFAULT_ZOOM_POS);
  const { width: wWidth, height: wHeight } = newState;
  const { height, whitePos } = Constants.silhouette;
  const canvasPositions = centerCanvasPosition(
    { canvasWidth, canvasHeight },
    { width: wWidth, height: wHeight + (height - whitePos) }
  );
  return ({
    ...newState,
    ...canvasPositions
  });
};

const canvasReducer = createReducer(getDefaultState(), {
  [Constants.canvas.add]: (state, {
    workId, workSizeId, frameId, x, y
  }) => ({
    ...state,
    works: [...state.works, {
      workId, workSizeId, frameId, key: (new Date()).getTime(), x, y, nonGlare: false
    }]
  }),
  [Constants.canvas.remove]: (state, { index }) => ({
    ...state,
    works: removeWork(state.works, index)
  }),
  [Constants.canvas.move]: (state, { index, x, y }) => ({
    ...state,
    works: updateWork(state.works, index, { x, y })
  }),
  [Constants.canvas.recalculateDpis]: (state) => {
    const { dpi, dpis } = recalculateDpis(state, state.zoomPosition);
    return ({
      ...calculateNewPos(state, dpi),
      dpis
    });
  },
  [Constants.canvas.zoomIn]: (state) => {
    const { zoomPosition } = state;
    if (zoomPosition === 0) { return state; }
    return setZoomPosition(state, zoomPosition - 1);
  },
  [Constants.canvas.zoomOut]: (state) => {
    const { dpis, zoomPosition } = state;
    if (zoomPosition === dpis.length - 1) { return state; }
    return setZoomPosition(state, zoomPosition + 1);
  },
  [Constants.canvas.resetCenter]: (
    state, { canvasWidth, canvasHeight }
  ) => resetCenter(state, { canvasWidth, canvasHeight }),
  [Constants.canvas.resetZoom]: (state) => {
    const newState = setZoomPosition(state, DEFAULT_ZOOM_POS);
    const {
      centerX, centerY, posX, posY
    } = newState;
    const canvasWidth = (centerX - posX) * 2;
    const canvasHeight = (centerY - posY) * 2;

    return resetCenter(newState, { canvasWidth, canvasHeight });
  },
  [Constants.canvas.setSize]: (state, { index, workSizeId }) => ({
    ...state,
    works: updateWork(state.works, index, { workSizeId, frameId: null })
  }),
  [Constants.canvas.setFrame]: (state, { index, frameId }) => ({
    ...state,
    works: updateWork(state.works, index, { frameId, nonGlare: false })
  }),
  [Constants.canvas.setNonGlare]: (state, { index, nonGlare }) => ({
    ...state,
    works: updateWork(state.works, index, { nonGlare })
  }),
  [Constants.canvas.setCenter]: (state, {
    centerX, centerY, posX, posY
  }) => ({
    ...state, centerX, centerY, posX, posY
  }),
  [Constants.canvas.hideSilhouette]: (state) => ({
    ...state,
    showSilhouette: false
  }),
  [Constants.canvas.setSilhouette]: (state, { silhouette }) => {
    const newState = setZoomPosition(state, DEFAULT_ZOOM_POS);
    const {
      centerX, centerY, posX, posY
    } = newState;
    const canvasWidth = (centerX - posX) * 2;
    const canvasHeight = (centerY - posY) * 2;

    return {
      ...resetCenter(newState, { canvasWidth, canvasHeight }),
      silhouette,
      showSilhouette: true
    };
  },
  [Constants.canvas.setColor]: (state, { currentColor }) => ({
    ...state,
    currentColor
  }),
  [Constants.canvas.showRuler]: (state) => ({
    ...state,
    rulerVisible: true
  }),
  [Constants.canvas.hideRuler]: (state) => ({
    ...state,
    rulerVisible: false
  }),
  [Constants.canvas.showGuides]: (state) => ({
    ...state,
    guidesVisible: true
  }),
  [Constants.canvas.hideGuides]: (state) => ({
    ...state,
    guidesVisible: false
  }),
  [Constants.canvas.setWidth]: (state, { width }) => {
    const newState = {
      ...state,
      width
    };
    const { dpi, dpis } = recalculateDpis(newState, DEFAULT_ZOOM_POS);
    return ({
      ...calculateNewPos(newState, dpi),
      dpis
    });
  },
  [Constants.canvas.setHeight]: (state, { height }) => {
    const newState = {
      ...state,
      height
    };
    const { dpi, dpis } = recalculateDpis(newState, DEFAULT_ZOOM_POS);
    return ({
      ...calculateNewPos(newState, dpi),
      dpis
    });
  },
  [Constants.canvas.showWidth]: (state) => ({ ...state, widthVisible: true }),
  [Constants.canvas.hideWidth]: (state) => ({ ...state, widthVisible: false }),
  [Constants.canvas.showHeight]: (state) => ({ ...state, heightVisible: true }),
  [Constants.canvas.hideHeight]: (state) => ({ ...state, heightVisible: false }),
  [Constants.canvas.updateAttributes]: (state, { attributes }) => ({
    ...state,
    ...attributes
  })
});

export default canvasReducer;
